import { css, html, LitElement } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { unsafeHTML } from 'lit/directives/unsafe-html.js'
import thermometer from './thermometer.raw.svg'

@customElement('temp-sensor')
export class TempSensor extends LitElement {
  // Kept in sync with the $temp Datastar signal via data-bind:temp
  @property({ type: Number }) temp: number = 0

  // Internal grid: 10 zones (5 cols × 2 rows), each 1.0 (cold) – 10.0 (hot)
  private grid: number[] = Array.from({ length: 10 }).fill(1.0) as number[]
  private doingTemp = false

  // Proportions loaded from localStorage before the first server temp arrives.
  // null once the live grid is established.
  private pendingProportions: number[] | null = null

  private readonly STORAGE_KEY = 'temp-sensor-grid-proportions'
  private readonly MIN = 0.0
  private readonly MAX = 10.0

  // Warming sensitivity: a base increment per pointermove event, plus a bonus
  // scaled by pointer velocity (px/ms). Velocity is absolute pixels so it feels
  // consistent regardless of component width.
  private readonly BASE_INCREMENT = 0.04 // floor warming per event
  private readonly VELOCITY_SCALE = 0.003 // extra increment per px/ms
  private readonly MAX_INCREMENT = 0.15 // cap to prevent single-event spikes

  // Pointer tracking for velocity calculation
  private _lastPointerX = 0
  private _lastPointerY = 0
  private _lastPointerTime = 0

  // Use light DOM so that temperature.css (targeting temp-sensor) applies directly.
  protected createRenderRoot() {
    return this
  }

  connectedCallback() {
    super.connectedCallback()
    this._loadProportions()
    this.addEventListener('pointerdown', this._onPointerDown)
    this.addEventListener('pointermove', this._onPointerMove)
    document.addEventListener('pointerup', this._onPointerUp)
    document.addEventListener('pointercancel', this._onPointerCancel)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.removeEventListener('pointerdown', this._onPointerDown)
    this.removeEventListener('pointermove', this._onPointerMove)
    document.removeEventListener('pointerup', this._onPointerUp)
    document.removeEventListener('pointercancel', this._onPointerCancel)
  }

  // Called by Lit whenever a property changes.
  // When the server pushes a cooled $temp via data-bind:temp, this fires.
  updated(changed: Map<string, unknown>) {
    if (changed.has('temp')) {
      this._applyCooledTemp(this.temp)
    }
  }

  // ── Scale helpers ──────────────────────────────────────────────────────────

  /** celsius (0–40) → internal grid value (1.0–10.0) */
  private _celsiusToInternal(c: number): number {
    return 1.0 + (Math.max(0, Math.min(40, c)) / 40.0) * 9.0
  }

  /** internal grid value (1.0–10.0) → celsius (0–40) */
  private _internalToCelsius(v: number): number {
    return ((Math.max(this.MIN, Math.min(this.MAX, v)) - 1.0) / 9.0) * 40.0
  }

  /**
   * Internal value → CSS variable reference.
   * --c1 is hot (red), --c10 is cool (blue), so we invert: c1 = value 10, c10 = value 1.
   */
  private _cssStop(v: number): string {
    const clamped = Math.max(0, Math.min(this.MAX, v))
    const stop = 11 - Math.max(1, Math.round(clamped))
    return `var(--c${stop})`
  }

  // ── CSS custom property updates ────────────────────────────────────────────

  private _updateAllCssVars() {
    this.grid.forEach((v, i) => {
      this.style.setProperty(`--grid${i + 1}`, this._cssStop(v))
    })
  }

  private _updateCssVar(idx: number) {
    this.style.setProperty(`--grid${idx + 1}`, this._cssStop(this.grid[idx]))
  }

  // ── localStorage ──────────────────────────────────────────────────────────

  /** Returns the current grid as proportions (each value / sum, sums to 1.0). */
  private _computeProportions(): number[] {
    const sum = this.grid.reduce((a, b) => a + b, 0)
    return this.grid.map((v) => v / sum)
  }

  private _saveProportions() {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this._computeProportions()))
    } catch {
      // ignore storage errors (private browsing, quota, etc.)
    }
  }

  private _loadProportions() {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY)
      if (stored) {
        const parsed: unknown = JSON.parse(stored)
        if (Array.isArray(parsed) && parsed.length === 10 && parsed.every((n) => typeof n === 'number')) {
          this.pendingProportions = parsed as number[]
        }
      }
    } catch {
      // ignore
    }
    // Render cold grid until first server update arrives
    this._updateAllCssVars()
  }

  // ── Server-driven cooling ─────────────────────────────────────────────────

  /**
   * Apply a server-supplied temperature (in °C) to the grid proportionally.
   * If localStorage proportions exist and haven't been applied yet, use those.
   * Otherwise scale the current live grid so its average matches the new temp.
   */
  private _applyCooledTemp(celsius: number) {
    const newAvg = this._celsiusToInternal(celsius)

    if (this.pendingProportions !== null) {
      // First server update after load — hydrate grid from stored proportions.
      // The proportions sum to 1.0; we want the resulting average to equal newAvg.
      // avg = sum(props[i] * scale) / 10 = scale / 10  →  scale = newAvg * 10
      const scale = newAvg * 10
      this.grid = this.pendingProportions.map((p) => Math.max(this.MIN, Math.min(this.MAX, p * scale)))
      this.pendingProportions = null
    } else {
      // Scale live grid proportionally so average matches newAvg.
      const currentAvg = this.grid.reduce((a, b) => a + b, 0) / 10
      if (currentAvg <= this.MIN) {
        // Grid is already fully cold — apply flat temperature.
        this.grid.fill(newAvg)
      } else {
        const ratio = newAvg / currentAvg
        this.grid = this.grid.map((v) => Math.max(this.MIN, Math.min(this.MAX, v * ratio)))
      }
    }

    this._updateAllCssVars()
    this._saveProportions()
  }

  // ── Pointer event handlers ────────────────────────────────────────────────

  private _onPointerDown = (evt: PointerEvent) => {
    this.doingTemp = true
    this._lastPointerX = evt.clientX
    this._lastPointerY = evt.clientY
    this._lastPointerTime = performance.now()
  }

  private _onPointerUp = () => {
    this.doingTemp = false
  }

  private _onPointerCancel = () => {
    this.doingTemp = false
  }

  private _onPointerMove = (evt: PointerEvent) => {
    if (!this.doingTemp) return

    const rect = this.getBoundingClientRect()
    const offsetX = evt.clientX - rect.left
    const offsetY = evt.clientY - rect.top

    const col = Math.min(4, Math.max(0, Math.floor(offsetX / (rect.width / 5))))
    const row = Math.min(1, Math.max(0, Math.floor(offsetY / (rect.height / 2))))
    const idx = col + 5 * row

    const now = performance.now()
    const dx = evt.clientX - this._lastPointerX
    const dy = evt.clientY - this._lastPointerY
    const dt = now - this._lastPointerTime
    const velocity = dt > 0 ? Math.sqrt(dx * dx + dy * dy) / dt : 0
    const increment = Math.min(this.MAX_INCREMENT, this.BASE_INCREMENT + this.VELOCITY_SCALE * velocity)
    this._lastPointerX = evt.clientX
    this._lastPointerY = evt.clientY
    this._lastPointerTime = now

    this.grid[idx] = Math.min(this.MAX, this.grid[idx] + increment)
    this._updateCssVar(idx)

    // Compute average and dispatch as celsius, rounded to 1 decimal place.
    const avgInternal = this.grid.reduce((a, b) => a + b, 0) / 10
    const celsius = Math.round(this._internalToCelsius(avgInternal) * 10) / 10

    this.dispatchEvent(
      new CustomEvent('temp-input', {
        detail: celsius,
        bubbles: true,
        composed: true,
      }),
    )

    this._saveProportions()
  }

  render() {
    return html`<span>${unsafeHTML(thermometer)}</span>`
  }
}
